// ============================================================================
// SETUP READINESS (ADR-0048 §2) — every required configuration for the active
// entity, grouped by capability, computed on read by the server. Keys are stable
// and documented in fairbooks-doc/reference/required-configuration.md.
// ============================================================================

export type SetupCapabilityCode = 'COMPANY' | 'BOOKS' | 'TAX' | 'INVOICING' | 'PURCHASING' | 'BANKING' | 'INVENTORY';
export type SetupCheckStatus = 'OK' | 'MISSING' | 'WARNING' | 'OPTIONAL' | 'NOT_APPLICABLE';

export interface SetupCheckDto {
  /** CAPABILITY.ITEM, e.g. TAX.JURISDICTION — quoted in refusals (SETUP_INCOMPLETE). */
  key: string;
  capability: SetupCapabilityCode;
  title: string;
  status: SetupCheckStatus;
  /** True when the capability cannot open without it. */
  required: boolean;
  message: string;
  /** Screen code to fix it on; null when managed outside FairBooks (tenant hub). */
  screenCode?: string | null;
  /** The permission the fixing screen needs. */
  permission?: string | null;
  /** required && status === 'MISSING' */
  blocks: boolean;
}

export interface SetupCapabilityDto {
  code: SetupCapabilityCode;
  title: string;
  description: string;
  /** Every required check here and in every dependency is OK. */
  ready: boolean;
  missingRequiredCount: number;
  checks: SetupCheckDto[];
}

export interface SetupReadinessDto {
  entityId: string;
  entityCode?: string | null;
  entityName?: string | null;
  businessUnitId?: string | null;
  evaluatedAt: string;
  capabilities: SetupCapabilityDto[];
  /** Every required check still missing across all capabilities — the sidebar badge. */
  missingRequiredCount: number;
}

/** The tenant-level view: one readiness per entity the user can access (ADR-0048 Slice 2). */
export interface SetupTenantReadinessDto {
  evaluatedAt: string;
  entities: SetupReadinessDto[];
  totalMissingRequiredCount: number;
}

/** The capabilities each one depends on (mirrors SetupCapabilities.DependsOn on the server). */
export const SETUP_DEPENDS_ON: Record<SetupCapabilityCode, SetupCapabilityCode[]> = {
  COMPANY: [],
  BOOKS: ['COMPANY'],
  TAX: ['COMPANY'],
  INVOICING: ['COMPANY', 'BOOKS', 'TAX'],
  PURCHASING: ['COMPANY', 'BOOKS', 'TAX'],
  BANKING: ['COMPANY', 'BOOKS'],
  INVENTORY: ['COMPANY', 'BOOKS'],
};

/** The checks that block a capability, including those of its dependencies, in stage order. */
export function blockingChecks(readiness: SetupReadinessDto, capability: SetupCapabilityCode): SetupCheckDto[] {
  const relevant = new Set<SetupCapabilityCode>([...SETUP_DEPENDS_ON[capability], capability]);
  return readiness.capabilities
    .filter(c => relevant.has(c.code))
    .flatMap(c => c.checks)
    .filter(c => c.blocks);
}
